import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import dayjs from '../../helpers/dates';
import axios from 'axios';
import {
  Row,
  Flex,
  Box,
  Card,
  Button,
  Input,
  Label,
} from '../../design-system';
import { Form } from 'react-final-form';
import Alert from '../../helpers/alert';
import {
  required,
  phoneNumberVal,
  composeValidators,
  emailVal,
  prettyPhone,
} from '../../helpers/utils';
import FieldColumn from '../../components/FieldColumn';
import {
  COUNTRY_OPTIONS,
  ERR_MSG,
  STATE_OPTIONS,
  US_STATES,
} from '../../globalConstants';
import { successRefetch } from '../../helpers/data-loader';

const StyledButton = styled(Button)`
  width: 9.375rem;
`;

interface AgencyProps {
  agency: any;
  refetch: () => void;
}

const Agency = ({ agency, refetch }: AgencyProps) => {
  const [agencyData, setAgency] = useState<any>(false);

  useEffect(() => {
    if (agency.agency) {
      setAgency(agency.agency);
    }
  }, [agency]);

  const onSubmit = async (values: any) => {
    try {
      await axios.put(`/agency/${values.id}`, values);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  return (
    <Card p={2} minHeight="48rem">
      <Card.Header fontSize={18}>{agencyData.name}</Card.Header>
      <Card.Body mt={4}>
        {agencyData && (
          <Form
            initialValues={{
              country: 'United States',
              ...agencyData,
              state:
                US_STATES.find(
                  ({ abbreviation }) => abbreviation === agencyData.state,
                )?.name || agencyData.state,
              phone: agencyData.phone && prettyPhone(agencyData.phone),
              fiscal_year_start:
                agencyData.fiscal_year_start || dayjs().format('YYYY-MM'),
            }}
            onSubmit={onSubmit}
            render={({ handleSubmit, values, form }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Row>
                    <FieldColumn
                      name="name"
                      label="Name"
                      validate={required}
                      width="100%"
                    />

                    <FieldColumn
                      name="address"
                      label="Address"
                      validate={required}
                      width="50%"
                    />
                    <FieldColumn
                      name="city"
                      label="City"
                      validate={required}
                      width="50%"
                    />
                    <FieldColumn
                      name="state"
                      label="State"
                      validate={required}
                      component="select"
                      disabled={!values.country}
                      options={
                        values.country ? STATE_OPTIONS(values.country) : []
                      }
                      width="50%"
                    />
                    <FieldColumn
                      name="country"
                      label="Country"
                      validate={required}
                      component="select"
                      options={COUNTRY_OPTIONS}
                      width="50%"
                      onChange={() => {
                        form.batch(() => {
                          form.change('state', null);
                        });
                      }}
                    />
                    <FieldColumn
                      name="zip"
                      label="Zip"
                      validate={required}
                      width="50%"
                    />
                    <FieldColumn
                      name="phone"
                      label="Phone"
                      validate={composeValidators(phoneNumberVal, required)}
                      transformOnBlur={prettyPhone}
                      width="50%"
                    />
                    <FieldColumn
                      name="email"
                      label="Email"
                      validate={composeValidators(emailVal, required)}
                      width="100%"
                    />
                    <div className="flex space-x-5 px-3">
                      <div className="flex flex-col">
                        <Label>Fiscal Year Start</Label>
                        <Input
                          value={values.fiscal_year_start}
                          name="fiscal_year_start"
                          type="month"
                          onChange={(_: any, newValue: any) => {
                            form.batch(() => {
                              form.change('fiscal_year_start', newValue);
                            });
                          }}
                        />
                      </div>
                    </div>
                  </Row>
                  <Box ml={2} pt={2}>
                    <Flex flexDirection="column" alignItems="center">
                      <StyledButton mt={3} aria-label="Save" type="submit">
                        Save
                      </StyledButton>
                    </Flex>
                  </Box>
                </form>
              );
            }}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default Agency;
